import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import { DescriptionSection } from '../../types/documentation';
import LinkedText from '../common/LinkedText';
import { parseTextWithLinks } from '../../utils/linkParser';

interface DescriptionSectionComponentProps {
  descriptionSection: DescriptionSection;
}

const DescriptionSectionComponent: React.FC<DescriptionSectionComponentProps> = ({
  descriptionSection
}) => {
  return (
    <Box sx={{ mb: 6 }}>
      <Paper
        elevation={1}
        sx={{
          p: 5,
          backgroundColor: '#fff',
          borderRadius: 3,
          border: '1px solid #f0f0f0',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            backgroundColor: '#1976d2',
          }
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 700, 
            color: '#1976d2',
            mb: 3,
            textAlign: 'left'
          }}
        >
          {descriptionSection.title}
        </Typography>
        
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: '1.2rem',
              lineHeight: 1.8,
              color: '#333',
              mb: 3,
              textAlign: 'left',
              fontWeight: 400
            }}
          >
            <LinkedText text={descriptionSection.mainDescription} />
          </Typography>
          
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              fontSize: '1.1rem',
              lineHeight: 1.7,
              color: '#555',
              mb: 3,
              textAlign: 'left'
            }}
          >
            <LinkedText text={descriptionSection.secondaryDescription} />
          </Typography>
        </Box>

        {/* Key Benefits Grid */}
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            mb: 4
          }}
        >
          {descriptionSection.benefits.map((benefit, index) => (
            <Box key={index} sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, textAlign: 'left' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                {benefit.title}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6 }}>
                <LinkedText text={benefit.description} />
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Technology Stack */}
        <Box sx={{ mt: 4, p: 3, backgroundColor: '#e3f2fd', borderRadius: 2 }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ fontWeight: 600, color: '#1976d2', mb: 2, textAlign: 'left' }}
          >
            Supported Technologies
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'flex-start' }}>
            {descriptionSection.supportedTechnologies.map((tech, index) => {
              // Parse the tech text to check if it contains a link
              const segments = parseTextWithLinks(tech);
              const hasLink = segments.some(seg => seg.type === 'link');
              const linkSegment = segments.find(seg => seg.type === 'link');
              
              // If tech contains a link, make the chip clickable
              if (hasLink && linkSegment && linkSegment.url) {
                const isExternal = linkSegment.external;
                return (
                  <Chip
                    key={index}
                    label={linkSegment.content}
                    size="small"
                    component="a"
                    href={linkSegment.url}
                    target={isExternal ? '_blank' : '_self'}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    clickable
                    sx={{
                      backgroundColor: '#fff',
                      color: '#1976d2',
                      fontWeight: 500,
                      border: '1px solid #1976d2',
                      textDecoration: 'none',
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                      }
                    }}
                  />
                );
              }
              
              // Regular chip without link
              return (
                <Chip
                  key={index}
                  label={tech}
                  size="small"
                  sx={{
                    backgroundColor: '#fff',
                    color: '#1976d2',
                    fontWeight: 500,
                    border: '1px solid #1976d2'
                  }}
                />
              );
            })}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default DescriptionSectionComponent;
